import { Service } from '@genial/services';
import { ErrorResponse } from '@genial/services/src';
import { Utils } from '@genial/utils';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useBase64Downloader } from '../../../../hooks/useBase64Downloader';
import { useToastCreator } from '../../../../hooks/useToastCreator';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../../master/selector';
import { statementOptionToMarketType } from '../constants';
import {
  updateStatementsFilterParamsSelector,
  updateStatementOptionSelector,
  updateIsExportingPdfForStatements,
} from '../selectors';
import {
  DownloadStatementPdfParams,
  StatementOptionQuery,
  StatementsFilterParamsProps,
} from '../types';

export const useStatementsActions = () => {
  const { showToast } = useToastCreator('download-statement-pdf-toast');
  const { downloadBase64 } = useBase64Downloader();

  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);

  const setIsExportingPdfForStatements = useSetRecoilState(updateIsExportingPdfForStatements);

  const setStatementOption = useSetRecoilState(updateStatementOptionSelector);
  const setStatementsFilterParams = useSetRecoilState(updateStatementsFilterParamsSelector);

  const updateStatementOption = (value: StatementOptionQuery) => {
    setStatementOption(value);
  };

  const updateStatementsFilterParams = (value: Partial<StatementsFilterParamsProps>) => {
    const data = value as StatementsFilterParamsProps;
    setStatementsFilterParams(data);
  };

  const handleDownloadStatementPdf = async ({
    accountNumber,
    filterType,
    statementOption,
    startDate,
    endDate,
    sharingType = 'Download',
  }: DownloadStatementPdfParams) => {
    const marketType = statementOptionToMarketType[statementOption];

    setIsExportingPdfForStatements(true);

    const handleDownloadError = (error?: ErrorResponse) => {
      showToast(
        'error',
        'Não foi possível processar sua solicitação.',
        error?.errorMessage || 'Não há informações disponíveis no momento.'
      );
    };
    try {
      const {
        data: { pdf, notificationSent },
      } = await Service.Finance.V1.getStatementByPdfAccountNumber({
        accountNumber,
        filterType,
        marketType,
        endDate,
        sharingType,
        startDate,
        advisorId: selectedAdvisor,
        partnerId: selectedPartner,
        screenName: 'customerDetails',
      });

      if (pdf === '' && notificationSent) {
        showToast(
          'success',
          'Extrato enviado com sucesso.',
          'O extrato foi enviado para o email do cliente.'
        );
        return;
      }

      if (!pdf || pdf === '') {
        handleDownloadError();
        return;
      }

      const initialDateFormatted = Utils.Mask.DateFormat(startDate, 'yyyy-MM-dd');
      const finalDateFormatted = Utils.Mask.DateFormat(endDate, 'yyyy-MM-dd');
      const fileName = `${accountNumber}_extrato_detalhados_${initialDateFormatted}_${finalDateFormatted}`;

      await downloadBase64(pdf, fileName, 'pdf');
    } catch (_error) {
      handleDownloadError();
    } finally {
      setIsExportingPdfForStatements(false);
    }
  };

  return {
    updateStatementOption,
    updateStatementsFilterParams,
    handleDownloadStatementPdf,
  };
};
